import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ChartLineUp,
  Warning,
  CheckCircle,
  ArrowRight,
  TrendUp,
  Brain,
  Target,
  Question,
  CaretDown,
  Eye,
  Lightbulb,
  Notebook,
  Clock,
  ListChecks,
  ArrowsClockwise,
  Gear,
  Crosshair,
  Table,
  Lightning,
  ChartBar,
  UserFocus,
  Path,
  Sparkle
} from 'phosphor-react';
import { updateSeoMetadata, getArticleAlternates } from '../../lib/seo';

const LOGO_HORIZONTAL = "https://customer-assets.emergentagent.com/job_chart-analyzer-107/artifacts/r9xkm2vj_cerebro%20%2B%20genilateral.png";

const DiarioDeTradesAutomatico = () => {
  useEffect(() => {
    updateSeoMetadata({
      title: 'Diário de Trades Automático | Como Melhorar Seus Resultados',
      description: 'Aprenda como um diário de trades automático pode melhorar sua consistência e resultados no trading.',
      path: '/pt/diario-de-trades-automatico',
      lang: 'pt-BR',
      alternates: getArticleAlternates('/pt/diario-de-trades-automatico'),
    });
    
    return () => {
      document.title = 'GENI — Turn trading data into better decisions';
    };
  }, []);

  const [openFaq, setOpenFaq] = React.useState(null);

  const faqs = [
    {
      question: "Preciso registrar todos os trades?",
      answer: "Idealmente, sim. Quanto mais dados você tiver, melhor será sua análise. Mas o mais importante é a consistência: é melhor registrar 80% dos trades de forma consistente do que 100% de forma irregular. Um diário de trades automático resolve esse problema ao capturar os dados automaticamente, sem depender da sua memória ou disciplina."
    },
    {
      question: "Planilha de trades funciona?",
      answer: "Planilhas funcionam para começar, mas têm limitações sérias. O preenchimento manual consome tempo, erros são comuns, e a análise fica limitada. Traders que usam planilhas frequentemente abandonam o hábito após algumas semanas. Um diário automático elimina o atrito e permite análises muito mais profundas."
    },
    {
      question: "Quanto tempo leva para ver resultados?",
      answer: "Com dados suficientes (50-100 trades), você já consegue identificar padrões importantes. Mas a melhoria real vem da consistência ao longo do tempo. Traders que mantêm um diário por 3-6 meses geralmente reportam melhorias significativas na tomada de decisão e nos resultados."
    },
    {
      question: "Diário de trades funciona para day trade e swing trade?",
      answer: "Sim, funciona para qualquer estilo de trading. A diferença está no volume de dados e nos padrões que você vai analisar. Day traders acumulam dados mais rapidamente e podem identificar padrões de horário e frequência. Swing traders focam mais em setups, condições de mercado e gestão de posição."
    }
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <nav className="border-b border-white/10 bg-[#0A0A0A]/95 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img 
              src={LOGO_HORIZONTAL}
              alt="GENI" 
              className="h-10 md:h-11 w-auto object-contain"
            />
          </Link>
          <Link 
            to="/signup"
            className="px-4 py-2 bg-[#00E676] text-black font-semibold rounded-sm hover:bg-[#00E676]/90 transition-colors text-sm"
          >
            Começar Grátis
          </Link>
        </div>
      </nav>

      <article className="max-w-3xl mx-auto px-6 pt-16 pb-20">
        
        <header className="mb-12">
          <div className="flex items-center gap-2 text-[#00E676] text-sm font-medium mb-4">
            <Notebook size={16} weight="fill" />
            <span>Controle de Trades</span>
          </div>
          
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-6">
            O Que é um Diário de Trades Automático
            <span className="block text-white/60 text-xl md:text-2xl mt-2">
              (E Por Que Você Deveria Usar)
            </span>
          </h1>
          
          <p className="text-lg text-white/70 leading-relaxed">
            A maioria dos traders brasileiros não registra seus trades. Alguns tentam usar 
            <strong className="text-white"> planilhas de trades</strong>, mas abandonam depois 
            de algumas semanas. Outros simplesmente operam no escuro, sem saber o que funciona 
            e o que não funciona. Essa falta de <strong className="text-white">controle de trades</strong> é 
            uma das principais razões pelas quais tantos traders não evoluem.
          </p>
        </header>

        {/* Introdução */}
        <section className="mb-14 prose prose-invert max-w-none">
          <p className="text-white/70 leading-relaxed text-lg">
            Um <strong className="text-white">diário de trades</strong> não é apenas um registro 
            de operações. É uma ferramenta de autoconhecimento que revela padrões no seu 
            comportamento, identifica seus pontos fortes e fracos, e fornece dados concretos 
            para melhorar sua performance.
          </p>
          <p className="text-white/70 leading-relaxed text-lg">
            O problema? Manter um <strong className="text-white">diário de trading</strong> manual 
            exige tempo e disciplina que poucos têm. É aí que entra o <strong className="text-white">diário 
            de trades automático</strong>—uma solução que captura e organiza seus dados sem 
            esforço, permitindo que você foque no que realmente importa: operar melhor.
          </p>
        </section>

        {/* Seção 1: O Que é um Diário de Trades */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-[#00E676] text-lg font-mono">01</span>
            O Que é um Diário de Trades
          </h2>
          
          <div className="bg-gradient-to-br from-[#00E676]/10 to-transparent border border-[#00E676]/20 rounded-lg p-8 mb-8">
            <p className="text-white/80 leading-relaxed mb-4">
              Um <strong className="text-white">diário de trades</strong> é um registro sistemático 
              de todas as suas operações no mercado. Ele inclui não apenas os dados básicos 
              (ativo, entrada, saída, resultado), mas também informações que ajudam a entender 
              o contexto de cada trade.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 gap-4 mb-8">
            {[
              { dado: "Dados da Operação", desc: "Ativo, preço de entrada, preço de saída, volume, horário" },
              { dado: "Setup Utilizado", desc: "Qual estratégia ou padrão motivou a entrada" },
              { dado: "Gestão de Risco", desc: "Stop loss, take profit, relação risco/retorno" },
              { dado: "Resultado", desc: "Lucro ou prejuízo em reais e em porcentagem" },
              { dado: "Estado Emocional", desc: "Como você se sentia antes e durante o trade" },
              { dado: "Observações", desc: "O que funcionou, o que não funcionou, aprendizados" }
            ].map((item, index) => (
              <div key={index} className="bg-[#121212] border border-white/10 rounded-lg p-4">
                <h4 className="font-semibold text-sm mb-1 text-[#00E676]">{item.dado}</h4>
                <p className="text-white/60 text-xs">{item.desc}</p>
              </div>
            ))}
          </div>
          
          <div className="prose prose-invert max-w-none">
            <p className="text-white/70 leading-relaxed">
              Quando você tem esses dados organizados, começa a ver padrões que seriam 
              invisíveis de outra forma. Talvez você opere melhor pela manhã. Talvez um 
              setup específico tenha taxa de acerto muito maior que outros. Sem dados, 
              você nunca saberia.
            </p>
          </div>
        </section>

        {/* Seção 2: Problemas do Controle Manual */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-red-400 text-lg font-mono">02</span>
            Problemas do Controle Manual
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4 mb-8">
            <p className="text-white/70 leading-relaxed">
              Muitos traders tentam usar <strong className="text-white">planilhas de trades</strong> no 
              Excel ou Google Sheets. A intenção é boa, mas a execução quase sempre falha. 
              Por quê?
            </p>
          </div>
          
          <div className="space-y-4 mb-8">
            {[
              {
                problema: "Consome Tempo Demais",
                description: "Preencher manualmente cada trade leva tempo. Depois de um dia intenso de operações, a última coisa que você quer é gastar mais 30 minutos preenchendo planilha.",
                icon: Clock
              },
              {
                problema: "Erros de Preenchimento",
                description: "Dados inseridos manualmente contêm erros. Um número digitado errado pode distorcer completamente sua análise.",
                icon: Warning
              },
              {
                problema: "Análise Limitada",
                description: "Planilhas não foram feitas para análise de trading. Criar gráficos, filtrar por setup, calcular métricas avançadas—tudo exige fórmulas complexas.",
                icon: Table
              },
              {
                problema: "Abandono Inevitável",
                description: "A maioria dos traders abandona a planilha em poucas semanas. O esforço não parece valer a pena, e o hábito nunca se consolida.",
                icon: ArrowsClockwise
              }
            ].map((item, index) => (
              <div 
                key={index}
                className="flex gap-4 items-start bg-[#121212] border border-red-500/20 rounded-lg p-6"
              >
                <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center flex-shrink-0">
                  <item.icon size={20} weight="duotone" className="text-red-400" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">{item.problema}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="prose prose-invert max-w-none">
            <p className="text-white/70 leading-relaxed">
              O resultado? Traders que sabem que deveriam registrar seus trades, mas não 
              conseguem manter a disciplina. E sem dados, não há como melhorar de forma 
              sistemática.
            </p>
          </div>
        </section>

        {/* Seção 3: O Que é um Diário de Trades Automático */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-[#00E676] text-lg font-mono">03</span>
            O Que é um Diário de Trades Automático
          </h2>
          
          <div className="bg-gradient-to-br from-[#00E676]/10 to-transparent border border-[#00E676]/20 rounded-lg p-8 mb-8">
            <p className="text-white/80 leading-relaxed mb-4">
              Um <strong className="text-white">diário de trades automático</strong> resolve os 
              problemas do controle manual ao capturar e organizar seus dados de forma 
              inteligente, com mínimo esforço da sua parte.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-3 gap-4 mb-8">
            {[
              {
                feature: "Captura Simplificada",
                description: "Insira trades rapidamente com poucos cliques ou importe diretamente de screenshots de gráficos.",
                icon: Lightning
              },
              {
                feature: "Organização Automática",
                description: "Os dados são categorizados automaticamente por setup, ativo, período e outras dimensões relevantes.",
                icon: Gear
              },
              {
                feature: "Análise Inteligente",
                description: "Métricas como taxa de acerto, profit factor e risco/retorno são calculadas automaticamente.",
                icon: ChartBar
              }
            ].map((item, index) => (
              <div 
                key={index}
                className="bg-[#121212] border border-[#00E676]/20 rounded-lg p-5"
              >
                <item.icon size={24} weight="duotone" className="text-[#00E676] mb-3" />
                <h4 className="font-semibold mb-2 text-sm">{item.feature}</h4>
                <p className="text-white/60 text-xs leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
          
          <div className="prose prose-invert max-w-none">
            <p className="text-white/70 leading-relaxed">
              A ideia é simples: remover todo o atrito possível para que você realmente 
              mantenha o hábito de registrar seus trades. Quando o processo é fácil, 
              você faz. Quando é difícil, você abandona.
            </p>
          </div>
        </section>

        {/* Seção 4: Por Que Traders Não Evoluem Sem Dados */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-orange-400 text-lg font-mono">04</span>
            Por Que Traders Não Evoluem Sem Dados
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4 mb-8">
            <p className="text-white/70 leading-relaxed">
              Existe um padrão comum entre traders que não evoluem: eles operam por meses 
              ou anos sem ter clareza sobre o que funciona e o que não funciona. A memória 
              é seletiva—lembramos dos grandes ganhos e esquecemos das pequenas perdas que 
              se acumulam.
            </p>
          </div>
          
          <div className="bg-[#121212] border border-orange-500/20 rounded-lg p-8 mb-8">
            <h3 className="font-semibold mb-4 text-orange-400 flex items-center gap-2">
              <Brain size={20} weight="duotone" />
              Sem Dados, Você Não Sabe...
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                "Qual sua taxa de acerto real",
                "Quais setups são lucrativos",
                "Em quais horários você opera melhor",
                "Qual seu risco/retorno médio",
                "Quais ativos você domina",
                "Quando você opera de forma emocional"
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-2">
                  <Warning size={14} weight="fill" className="text-orange-400 mt-0.5 flex-shrink-0" />
                  <span className="text-white/70 text-sm">{item}</span>
                </div>
              ))}
            </div>
          </div>
          
          <div className="prose prose-invert max-w-none">
            <p className="text-white/70 leading-relaxed">
              Traders profissionais tratam dados como ouro. Eles sabem exatamente quais 
              setups produzem resultado, quais condições de mercado favorecem seu estilo, 
              e onde estão seus pontos cegos. Essa clareza vem de um <strong className="text-white">diário 
              de trading</strong> bem mantido.
            </p>
          </div>
        </section>

        {/* Seção 5: Benefícios do Diário Automático */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-[#00E676] text-lg font-mono">05</span>
            Benefícios do Diário Automático
          </h2>
          
          <div className="space-y-4 mb-8">
            {[
              {
                beneficio: "Identificar Padrões de Comportamento",
                description: "Descubra quando você opera bem e quando opera mal. Veja correlações entre estado emocional, horário, e resultados.",
                icon: Eye
              },
              {
                beneficio: "Eliminar Setups que Não Funcionam",
                description: "Pare de perder dinheiro com estratégias que parecem boas mas não produzem resultado. Os dados mostram a verdade.",
                icon: Target
              },
              {
                beneficio: "Construir Confiança com Evidências",
                description: "Saiba exatamente o que funciona baseado em dados reais, não em achismo. Isso reduz dúvidas e melhora a execução.",
                icon: ChartLineUp
              },
              {
                beneficio: "Acelerar a Curva de Aprendizado",
                description: "Traders com diário evoluem mais rápido porque aprendem com cada operação. Sem diário, os mesmos erros se repetem indefinidamente.",
                icon: TrendUp
              }
            ].map((item, index) => (
              <div 
                key={index}
                className="flex gap-4 items-start bg-[#121212] border border-[#00E676]/20 rounded-lg p-6"
              >
                <div className="w-10 h-10 rounded-lg bg-[#00E676]/10 flex items-center justify-center flex-shrink-0">
                  <item.icon size={20} weight="duotone" className="text-[#00E676]" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">{item.beneficio}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Seção 6: O Que um Bom Diário Deve Ter */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-[#00E676] text-lg font-mono">06</span>
            O Que um Bom Diário Deve Ter
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4 mb-8">
            <p className="text-white/70 leading-relaxed">
              Nem todo <strong className="text-white">diário de trades</strong> é igual. Para 
              realmente ajudar na sua evolução, um bom diário precisa ter características 
              específicas:
            </p>
          </div>
          
          <div className="bg-[#121212] border border-white/10 rounded-lg p-6 mb-8">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <ListChecks size={20} weight="duotone" className="text-[#00E676]" />
              Características Essenciais
            </h3>
            <div className="space-y-3">
              {[
                { item: "Facilidade de Uso", desc: "Se for difícil usar, você não vai usar. Simplicidade é fundamental." },
                { item: "Métricas Automáticas", desc: "Cálculo automático de taxa de acerto, profit factor, drawdown e outras métricas importantes." },
                { item: "Filtros e Segmentação", desc: "Capacidade de filtrar por setup, ativo, período, resultado. A análise precisa ser granular." },
                { item: "Visualização Clara", desc: "Gráficos e dashboards que mostram sua performance de forma visual e intuitiva." },
                { item: "Histórico Completo", desc: "Acesso a todos os seus trades passados para análise de longo prazo." }
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle size={16} weight="fill" className="text-[#00E676] mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="text-white font-medium text-sm">{item.item}:</span>
                    <span className="text-white/60 text-sm ml-1">{item.desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Seção 7: Manual vs Automático */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-[#00E676] text-lg font-mono">07</span>
            Manual vs Automático
          </h2>
          
          <div className="bg-[#121212] border border-white/10 rounded-lg overflow-hidden mb-8">
            <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-white/10">
              <div className="p-6">
                <h4 className="font-semibold mb-4 text-orange-400 flex items-center gap-2">
                  <Table size={18} />
                  Diário Manual (Planilha)
                </h4>
                <ul className="space-y-2 text-white/60 text-sm">
                  <li className="flex items-start gap-2">
                    <Warning size={14} weight="fill" className="text-orange-400 mt-0.5 flex-shrink-0" />
                    Preenchimento demorado
                  </li>
                  <li className="flex items-start gap-2">
                    <Warning size={14} weight="fill" className="text-orange-400 mt-0.5 flex-shrink-0" />
                    Erros de digitação comuns
                  </li>
                  <li className="flex items-start gap-2">
                    <Warning size={14} weight="fill" className="text-orange-400 mt-0.5 flex-shrink-0" />
                    Análise limitada
                  </li>
                  <li className="flex items-start gap-2">
                    <Warning size={14} weight="fill" className="text-orange-400 mt-0.5 flex-shrink-0" />
                    Alto risco de abandono
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={14} weight="fill" className="text-green-400 mt-0.5 flex-shrink-0" />
                    Gratuito
                  </li>
                </ul>
              </div>
              <div className="p-6">
                <h4 className="font-semibold mb-4 text-[#00E676] flex items-center gap-2">
                  <Sparkle size={18} />
                  Diário Automático
                </h4>
                <ul className="space-y-2 text-white/60 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle size={14} weight="fill" className="text-[#00E676] mt-0.5 flex-shrink-0" />
                    Registro rápido e fácil
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={14} weight="fill" className="text-[#00E676] mt-0.5 flex-shrink-0" />
                    Dados precisos e consistentes
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={14} weight="fill" className="text-[#00E676] mt-0.5 flex-shrink-0" />
                    Análise avançada automática
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={14} weight="fill" className="text-[#00E676] mt-0.5 flex-shrink-0" />
                    Maior chance de manter o hábito
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={14} weight="fill" className="text-[#00E676] mt-0.5 flex-shrink-0" />
                    Investimento que se paga
                  </li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="prose prose-invert max-w-none">
            <p className="text-white/70 leading-relaxed">
              A escolha parece óbvia, mas muitos traders hesitam em investir em ferramentas. 
              A realidade é que o custo de não ter dados—operações repetidamente erradas, 
              setups não otimizados, falta de evolução—é muito maior que qualquer assinatura.
            </p>
          </div>
        </section>

        {/* Seção 8: Evolução do Trader */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-[#00E676] text-lg font-mono">08</span>
            Evolução do Trader
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4 mb-8">
            <p className="text-white/70 leading-relaxed">
              A jornada de todo trader segue um padrão. Quem usa dados evolui mais rápido 
              e de forma mais consistente:
            </p>
          </div>
          
          <div className="bg-[#121212] border border-white/10 rounded-lg p-6 mb-8">
            <div className="space-y-4">
              {[
                { fase: "Fase 1: Operando às Cegas", desc: "Sem registro, sem análise. Cada dia é um novo começo, sem aprendizado acumulado.", color: "red" },
                { fase: "Fase 2: Começando a Registrar", desc: "Tentativas de planilha ou anotações. Alguma consciência, mas inconsistente.", color: "orange" },
                { fase: "Fase 3: Dados Consistentes", desc: "Registro sistemático com ferramenta adequada. Padrões começam a aparecer.", color: "yellow" },
                { fase: "Fase 4: Análise e Ajustes", desc: "Uso ativo dos dados para tomar decisões. Eliminação de setups ruins, foco nos bons.", color: "green" },
                { fase: "Fase 5: Otimização Contínua", desc: "Refinamento constante baseado em dados. Evolução acelerada e sustentável.", color: "emerald" }
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-4">
                  <div className={`w-3 h-3 rounded-full mt-1.5 flex-shrink-0 ${
                    item.color === 'red' ? 'bg-red-400' :
                    item.color === 'orange' ? 'bg-orange-400' :
                    item.color === 'yellow' ? 'bg-yellow-400' :
                    item.color === 'green' ? 'bg-green-400' :
                    'bg-emerald-400'
                  }`} />
                  <div>
                    <h4 className="font-semibold text-sm mb-1">{item.fase}</h4>
                    <p className="text-white/60 text-sm">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="prose prose-invert max-w-none">
            <p className="text-white/70 leading-relaxed">
              O <strong className="text-white">GENI</strong> é um diário de trades moderno que 
              ajuda traders a automatizar e entender sua performance com mais clareza. Ele foi 
              criado para resolver exatamente os problemas que discutimos aqui—tornando o 
              registro fácil e a análise poderosa.
            </p>
          </div>
        </section>

        {/* Conclusão */}
        <section className="mb-14">
          <div className="bg-gradient-to-br from-[#00E676]/10 to-transparent border border-[#00E676]/30 rounded-lg p-8">
            <h3 className="text-xl font-bold mb-4">Conclusão</h3>
            <p className="text-white/80 leading-relaxed mb-4">
              Se você quer evoluir no trading, começar a analisar seus trades é essencial. 
              Não dá para melhorar aquilo que você não mede. Um <strong className="text-white">diário 
              de trades automático</strong> remove o atrito e permite que você foque no que 
              realmente importa: tomar decisões melhores.
            </p>
            <p className="text-white/70 leading-relaxed">
              Os traders que tratam isso como prioridade são os que eventualmente conseguem 
              consistência. Os que ignoram continuam repetindo os mesmos erros, ano após ano.
            </p>
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-8 flex items-center gap-3">
            <Question size={28} weight="duotone" className="text-purple-400" />
            Perguntas Frequentes
          </h2>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="bg-[#121212] border border-white/10 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-white/5 transition-colors"
                >
                  <span className="font-medium pr-4">{faq.question}</span>
                  <CaretDown 
                    size={18} 
                    className={`text-white/40 transition-transform flex-shrink-0 ${openFaq === index ? 'rotate-180' : ''}`} 
                  />
                </button>
                {openFaq === index && (
                  <div className="px-5 pb-5 pt-0">
                    <p className="text-white/60 text-sm leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section>
          <div className="bg-gradient-to-br from-[#00E676]/10 via-[#121212] to-purple-500/10 border border-white/10 rounded-lg p-8 md:p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#00E676]/20 flex items-center justify-center">
              <Notebook size={32} weight="duotone" className="text-[#00E676]" />
            </div>
            
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              Comece a Evoluir Hoje
            </h2>
            
            <p className="text-white/70 max-w-xl mx-auto mb-8 leading-relaxed">
              Chega de operar no escuro. Registre seus trades, analise sua performance, 
              e tome decisões baseadas em dados reais.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/signup"
                className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-[#00E676] text-black font-semibold rounded-sm hover:bg-[#00E676]/90 transition-colors"
              >
                Começar Grátis
                <ArrowRight size={18} weight="bold" />
              </Link>
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-white/10 text-white font-semibold rounded-sm hover:bg-white/20 transition-colors"
              >
                Saiba Mais
              </Link>
            </div>
            
            <p className="text-white/40 text-xs mt-6">
              Sem cartão de crédito · Plano gratuito disponível
            </p>
          </div>
        </section>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Article",
              "headline": "O Que é um Diário de Trades Automático (E Por Que Você Deveria Usar)",
              "description": "Aprenda como um diário de trades automático pode melhorar sua consistência e resultados no trading.",
              "inLanguage": "pt-BR",
              "author": {
                "@type": "Organization",
                "name": "GENI"
              },
              "publisher": {
                "@type": "Organization",
                "name": "GENI",
                "logo": {
                  "@type": "ImageObject",
                  "url": "https://genitradeapp.com/geni-logo.png"
                }
              },
              "datePublished": "2026-01-01",
              "dateModified": new Date().toISOString().split('T')[0],
              "mainEntityOfPage": {
                "@type": "WebPage",
                "@id": "https://genitradeapp.com/pt/diario-de-trades-automatico"
              }
            })
          }}
        />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": faqs.map(faq => ({
                "@type": "Question",
                "name": faq.question,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": faq.answer
                }
              }))
            })
          }}
        />
      </article>

      <footer className="border-t border-white/10 py-8">
        <div className="max-w-4xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center">
            <img 
              src={LOGO_HORIZONTAL}
              alt="GENI" 
              className="h-6 opacity-60"
            />
          </Link>
          <div className="flex items-center gap-6 text-sm text-white/40">
            <Link to="/privacy" className="hover:text-white/60 transition-colors">Privacidade</Link>
            <Link to="/terms" className="hover:text-white/60 transition-colors">Termos</Link>
            <span>© 2026 GENI</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DiarioDeTradesAutomatico;
